import {useGeoSearch, useSearchBox} from "react-instantsearch";
import type {GeoHit} from "instantsearch.js";
import {useEffect, useState} from "react";
import {ControlPosition, Map, useMap} from "@vis.gl/react-google-maps";
import useDetectedLocationFromResults from "../hooks/useIPGeoLocation.tsx";
import PracticesMarkers from "./PracticesMarkers.tsx";
import {useApp} from "../providers/AppProvider.tsx";
import {debounce} from "lodash-es";
import clsx from "clsx";


export interface Practice {
    id: number
    address: string
    external_id: number
    efko_guid: string
    unique_name: string
    name: string
    phone: string
    status?: string
    tenant_id: number
    slug: string
    third_party_connections: ThirdPartyConnection[]
    location: PracticeLocation
    _geoloc: Geoloc
    phone_formatted: string
    _tags: string[]
    objectID: string
}

export interface Geoloc {
    lat: number
    lng: number
}


export interface PracticeLocation {
    formatted_address: string
    subpremise: string
    street_number: string
    route: string
    locality: string
    administrative_area_level_1: string
    country: string
    postal_code: string
    postal_code_suffix: string
    metadata?: LocationMetadata
    place_id: string
}

export interface LocationMetadata {
    maps_uri: string
    reviews: Review[]
    photos: string[]
    google_maps_links: GoogleMapsLinks
    hours: Hours
    website_uri: string
}

export interface Hours {
    openNow: boolean
    periods: Period[]
    weekdayDescriptions: string[]
    nextOpenTime: string
}

export interface Period {
    open: Open
    close: Close
}

export interface Open {
    day: number
    hour: number
    minute: number
}

export interface Close {
    day: number
    hour: number
    minute: number
}

export interface Review {
    name: string
    relativePublishTimeDescription: string
    rating: number
    text: Text
    originalText: OriginalText
    authorAttribution: AuthorAttribution
    publishTime: string
    flagContentUri: string
    googleMapsUri: string
}

export interface Text {
    text: string
    languageCode: string
}

export interface OriginalText {
    text: string
    languageCode: string
}

export interface AuthorAttribution {
    displayName: string
    uri: string
    photoUri: string
}

export interface Photo {
    name: string
    widthPx: number
    heightPx: number
    authorAttributions: AuthorAttribution2[]
    flagContentUri: string
    googleMapsUri: string
}

export interface AuthorAttribution2 {
    displayName: string
    uri: string
    photoUri: string
}

export interface GoogleMapsLinks {
    directionsUri: string
    placeUri: string
    writeAReviewUri: string
    reviewsUri: string
    photosUri: string
}

export interface ThirdPartyConnection {
    provider: string
    external_id: string
}

export interface Geoloc {
    lat: number
    lng: number
}


const PracticesMapContainer = () => {
    const {query, refine: refineQuery} = useSearchBox();
    const locationFromResults = useDetectedLocationFromResults()
    const [userInteracted, setUserInteracted] = useState<boolean>(false);
    const [shouldLoadMap, setShouldLoadMap] = useState(false);


    const map = useMap();

    const [skipFirstEventOnMobile, setSkipFirstEventOnMobile] = useState<boolean>(true);

    const {state} = useApp()

    useEffect(() => {
        if (state.isMobileDevice) {
            const timer = setTimeout(() => setShouldLoadMap(true), 100);
            return () => clearTimeout(timer);
        } else {
            setShouldLoadMap(true);
        }
    }, [state?.isMobileDevice]);


    useEffect(() => {

        if (!map || !locationFromResults) return;
        if (userInteracted) return

        map.setCenter({
            lat: locationFromResults?.lat ?? 0,
            lng: locationFromResults?.lng ?? 0
        })
        setUserInteracted(true);
        // do something with the map instance
    }, [locationFromResults, map, userInteracted]);

    useEffect(() => {
        if (state.userPickedLocation) {
            if (state.userPickedLocation.geometry?.location) {
                map?.setCenter(state.userPickedLocation.geometry?.location)
                map?.setZoom(12)
            }
        }
    }, [map, state.userPickedLocation]);

    const {
        items,
        refine: refineItems,
    } = useGeoSearch<GeoHit<Practice>>();


    // When the user moves the map, we clear the query if necessary to only
    // refine on the new boundaries of the map.
    const onViewChange = () => {
        if (!userInteracted) return;

        if (state.isMobileDevice && skipFirstEventOnMobile) {
            setSkipFirstEventOnMobile(false);
            return;
        }

        if (query.length > 0) {
            refineQuery('');
        }

        const northEast = map?.getBounds()?.getNorthEast().toJSON()
        const southWest = map?.getBounds()?.getSouthWest().toJSON()

        if (northEast && southWest) {
            refineItems({northEast, southWest})
        }
    };


    useEffect(() => {
        const userEnteredSearch = map?.get('userEnteredSearch')
        if (userEnteredSearch > 0 && userEnteredSearch < 3) {
            console.log(items.length)
            // @ts-expect-error nvm
            if (items.length < (state.isMobileDevice ? 2 : 3) && map?.getZoom() > 4) {
                const zoom = map?.getZoom()
                if (zoom) {
                    map?.setZoom(zoom - 0.2)
                }
            } else {
                map?.set('userEnteredSearch', userEnteredSearch + 1)
            }
        }
    }, [items, map, state.isMobileDevice]);

    return (
        <div style={{height: state.isMobileDevice ? '60dvh' : '100dvh'}}
             className={clsx({'px-4 pt-4': state.isMobileDevice})}
        >
            {shouldLoadMap && <Map
                clickableIcons={false}
                className={clsx({'h-full rounded-2xl overflow-hidden': state.isMobileDevice})}
                gestureHandling={"greedy"}
                streetViewControl={false}
                fullscreenControl={false}
                mapId={'5245ef91826603b0d1ffedbd'}
                defaultZoom={state.isMobileDevice ? 11.5 : 12}
                maxZoom={14}
                disableDefaultUI={state.isMobileDevice}
                minZoom={6}
                // renderingType={state.isMobileDevice ? "RASTER" : "VECTOR"}
                defaultCenter={{lat: 0, lng: 0}}
                onCameraChanged={debounce(() => {
                    if (map?.get('autoPan')) {
                        map?.setValues({autoPan: undefined})
                        return
                    }
                    onViewChange()
                }, 500)}
                mapTypeControlOptions={{
                    position: ControlPosition.TOP_RIGHT
                }}
                mapTypeControl={!state.isMobileDevice}
            >

                <PracticesMarkers practices={items}/>
            </Map>}
        </div>
    );
}

export default PracticesMapContainer;